// Netcode profiler HUD + controls.
//
// Renders a small fixed-position overlay with live metrics and three
// buttons (Start / Stop / Export). Toggled with F9. Does not depend on any
// UI library — plain DOM, keeps bundle small.
//
// Layout:
//
//   ┌───────────────────────────────┐
//   │ NETCODE  ● REC 00:12.3        │
//   │  FPS         143.1            │
//   │  Tick/s       60.0            │
//   │  Patch/s      59.2            │
//   │  Ping          13 ms          │
//   │  Drift       0.042 m          │
//   │  Rollback/s    2.1            │
//   │ [Start] [Stop] [Export]       │
//   └───────────────────────────────┘

import { netcodeProfiler } from '../profile/NetcodeProfiler';
import type { ColyseusTransport } from '../net/ColyseusTransport';
import type {
  CombinedDump, DumpMeta, ServerProfile
} from '../profile/types';
import { PROFILER_VERSION } from '../profile/types';

export interface ProfilerHudOpts {
  /** Transport to drive. If null, export button is disabled. */
  transport: ColyseusTransport | null;
  /** Optional mapId to surface in the dump meta. */
  mapId?: string;
  /** Optional client build version / git hash. */
  clientVersion?: string;
}

const HOTKEY = 'F9';

export class ProfilerHud {
  private rootEl: HTMLElement;
  private statusEl: HTMLElement;
  private metricEls: Record<string, HTMLElement> = {};
  private btnStart: HTMLButtonElement;
  private btnStop: HTMLButtonElement;
  private btnExport: HTMLButtonElement;
  private rafHandle = 0;
  private transport: ColyseusTransport | null;
  private mapId: string;
  private clientVersion: string;
  private exporting = false;
  private visible = true;

  constructor(opts: ProfilerHudOpts) {
    this.transport = opts.transport;
    this.mapId = opts.mapId ?? '';
    this.clientVersion = opts.clientVersion ?? '';

    this.rootEl = document.createElement('div');
    this.rootEl.id = 'netcode-profiler-hud';
    Object.assign(this.rootEl.style, {
      position: 'fixed',
      top: '8px',
      right: '8px',
      minWidth: '210px',
      padding: '8px 10px',
      background: 'rgba(8, 10, 18, 0.88)',
      color: '#e8f0ff',
      font: '11px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace',
      border: '1px solid rgba(120, 140, 180, 0.25)',
      borderRadius: '6px',
      zIndex: '99999',
      userSelect: 'none',
      pointerEvents: 'auto',
      letterSpacing: '0.2px'
    } as Partial<CSSStyleDeclaration>);

    const title = document.createElement('div');
    title.textContent = 'NETCODE';
    Object.assign(title.style, {
      fontWeight: 'bold',
      letterSpacing: '1.5px',
      color: '#7ea1ff',
      marginBottom: '4px',
      display: 'inline-block'
    } as Partial<CSSStyleDeclaration>);

    this.statusEl = document.createElement('span');
    this.statusEl.textContent = '  idle';
    Object.assign(this.statusEl.style, {
      marginLeft: '8px',
      color: '#9aa6bd'
    } as Partial<CSSStyleDeclaration>);

    const titleRow = document.createElement('div');
    titleRow.append(title, this.statusEl);
    this.rootEl.appendChild(titleRow);

    const grid = document.createElement('div');
    grid.style.display = 'grid';
    grid.style.gridTemplateColumns = '90px 1fr';
    grid.style.rowGap = '1px';
    grid.style.margin = '4px 0';
    const rows: Array<[string, string]> = [
      ['fps', 'FPS'],
      ['tickHz', 'Tick/s'],
      ['patchHz', 'Patch/s'],
      ['bonesHz', 'Bones/s'],
      ['ping', 'Ping'],
      ['drift', 'Drift'],
      ['rollbackHz', 'Rollback/s'],
      ['interpDelay', 'Interp'],
      ['queueDepth', 'Rem Q'],
      ['rafFrames', 'rAF rec'],
      ['simTicks', 'Sim rec']
    ];
    for (const [key, label] of rows) {
      const lbl = document.createElement('div');
      lbl.textContent = label;
      lbl.style.color = '#9aa6bd';
      const val = document.createElement('div');
      val.textContent = '—';
      val.style.textAlign = 'right';
      val.style.color = '#e8f0ff';
      grid.append(lbl, val);
      this.metricEls[key] = val;
    }
    this.rootEl.appendChild(grid);

    const btnRow = document.createElement('div');
    Object.assign(btnRow.style, {
      display: 'flex',
      gap: '4px',
      marginTop: '6px'
    } as Partial<CSSStyleDeclaration>);
    this.btnStart = this.makeBtn('Start');
    this.btnStop = this.makeBtn('Stop');
    this.btnExport = this.makeBtn('Export');
    this.btnStart.addEventListener('click', () => this.startRecording());
    this.btnStop.addEventListener('click', () => this.stopRecording());
    this.btnExport.addEventListener('click', () => this.exportDump());
    btnRow.append(this.btnStart, this.btnStop, this.btnExport);
    this.rootEl.appendChild(btnRow);

    const hint = document.createElement('div');
    hint.textContent = `Toggle with ${HOTKEY}`;
    Object.assign(hint.style, {
      marginTop: '4px',
      color: '#6a7689',
      fontSize: '10px',
      textAlign: 'right'
    } as Partial<CSSStyleDeclaration>);
    this.rootEl.appendChild(hint);

    document.body.appendChild(this.rootEl);
    window.addEventListener('keydown', this.onKeyDown);
    this.tick();
  }

  dispose(): void {
    cancelAnimationFrame(this.rafHandle);
    window.removeEventListener('keydown', this.onKeyDown);
    this.rootEl.remove();
  }

  setTransport(t: ColyseusTransport | null): void {
    this.transport = t;
  }

  toggleVisible(): void {
    this.visible = !this.visible;
    this.rootEl.style.display = this.visible ? '' : 'none';
  }

  // ------------------------------------------------------------------------

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === HOTKEY || e.code === HOTKEY) {
      e.preventDefault();
      this.toggleVisible();
    }
  };

  private makeBtn(label: string): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = label;
    Object.assign(b.style, {
      flex: '1',
      padding: '3px 0',
      background: '#1a2a4a',
      color: '#e8f0ff',
      border: '1px solid #2a3e6a',
      borderRadius: '3px',
      cursor: 'pointer',
      font: 'inherit'
    } as Partial<CSSStyleDeclaration>);
    return b;
  }

  private tick = (): void => {
    this.rafHandle = requestAnimationFrame(this.tick);

    const recording = netcodeProfiler.isRecording();
    const durMs = netcodeProfiler.getDurationMs();
    this.statusEl.textContent = recording
      ? `  ● REC ${formatDur(durMs)}`
      : this.exporting
        ? '  ⟳ exporting…'
        : '  idle';
    this.statusEl.style.color = recording ? '#ff7070' : '#9aa6bd';

    const fps = netcodeProfiler.currentFps();
    const tickHz = netcodeProfiler.currentTickHz();
    const rollbackHz = netcodeProfiler.currentRollbackHz();

    this.metricEls.fps!.textContent = fps > 0 ? fps.toFixed(1) : '—';
    this.metricEls.tickHz!.textContent = tickHz > 0 ? tickHz.toFixed(1) : '—';
    this.metricEls.rollbackHz!.textContent = rollbackHz.toFixed(0) + '/s';

    const t = this.transport;
    if (t) {
      const ping = t.pingFor(t.localPlayerIds()[0]!);
      this.metricEls.ping!.textContent = ping > 0 ? ping.toFixed(0) + ' ms' : '—';
      this.metricEls.drift!.textContent = t.lastReconcileDrift.toFixed(3) + ' m';
      // The interp delay and patch/bones rates are read directly off the
      // recording profile's most recent entries when recording; when not
      // recording, we fall back to '—'.
      if (recording) {
        const prof = netcodeProfiler.getProfile();
        const patches = prof.patches.wallTimeMs;
        const bones = prof.bonesMessages.wallTimeMs;
        this.metricEls.patchHz!.textContent = inRateLast1s(patches).toFixed(1);
        this.metricEls.bonesHz!.textContent = inRateLast1s(bones).toFixed(1);
        this.metricEls.rafFrames!.textContent = prof.rafFrames.idx.length.toString();
        this.metricEls.simTicks!.textContent = prof.simTicks.clientTick.length.toString();
        // Remote queue depth (from the newest per-remote sample).
        const depths: string[] = [];
        for (const id of Object.keys(prof.remoteChars)) {
          const arr = prof.remoteChars[Number(id)]!.inputQueueDepthBefore;
          if (arr.length > 0) depths.push(arr[arr.length - 1]!.toString());
        }
        this.metricEls.queueDepth!.textContent = depths.length > 0 ? depths.join('/') : '—';
      } else {
        this.metricEls.patchHz!.textContent = '—';
        this.metricEls.bonesHz!.textContent = '—';
        this.metricEls.rafFrames!.textContent = '—';
        this.metricEls.simTicks!.textContent = '—';
        this.metricEls.queueDepth!.textContent = '—';
      }
    } else {
      this.metricEls.ping!.textContent = '—';
      this.metricEls.drift!.textContent = '—';
      this.metricEls.patchHz!.textContent = '—';
      this.metricEls.bonesHz!.textContent = '—';
    }
    this.metricEls.interpDelay!.textContent = '—';

    // Button enable/disable state.
    this.btnStart.disabled = recording || !this.transport;
    this.btnStop.disabled = !recording;
    this.btnExport.disabled = this.exporting || !this.transport || netcodeProfiler.getProfile().simTicks.clientTick.length === 0;
    // Show disabled buttons as dimmed.
    [this.btnStart, this.btnStop, this.btnExport].forEach((b) => {
      b.style.opacity = b.disabled ? '0.4' : '1';
      b.style.cursor = b.disabled ? 'not-allowed' : 'pointer';
    });
  };

  // ------------------------------------------------------------------------
  // Actions

  private startRecording(): void {
    if (!this.transport) return;
    if (netcodeProfiler.isRecording()) return;
    const sessionId = randomSessionId();
    this.transport.profileStart(sessionId);
  }

  private stopRecording(): void {
    if (!this.transport) return;
    this.transport.profileStop();
  }

  private async exportDump(): Promise<void> {
    if (!this.transport) return;
    if (this.exporting) return;
    this.exporting = true;
    try {
      const clientProfile = netcodeProfiler.getProfile();
      const localCharId = this.transport.localPlayerIds()[0]!;
      let serverProfile: ServerProfile | { error: string };
      let serverMeta: { tickHz: number; mapId: string; serverStartAtMs: number; requesterCharId: number; sessionId: string; durationMs: number };
      try {
        const dump = await this.transport.requestProfileDump();
        serverProfile = dump.profile;
        serverMeta = {
          tickHz: dump.tickHz,
          mapId: dump.mapId,
          serverStartAtMs: dump.serverStartAtMs,
          requesterCharId: dump.requesterCharId,
          sessionId: dump.sessionId,
          durationMs: dump.durationMs
        };
      } catch (e) {
        serverProfile = { error: (e as Error).message };
        serverMeta = {
          tickHz: 60,
          mapId: this.mapId,
          serverStartAtMs: 0,
          requesterCharId: localCharId,
          sessionId: netcodeProfiler.getSessionId(),
          durationMs: Math.round(netcodeProfiler.getDurationMs())
        };
      }

      const meta: DumpMeta = {
        schemaVersion: PROFILER_VERSION,
        sessionId: netcodeProfiler.getSessionId() || serverMeta.sessionId,
        recordedAtMs: Date.now() - serverMeta.durationMs,
        durationMs: serverMeta.durationMs,
        clientTickCount: clientProfile.simTicks.clientTick.length,
        serverTickCount: 'ticks' in (serverProfile as ServerProfile)
          ? (serverProfile as ServerProfile).ticks.serverTick.length
          : 0,
        localCharId,
        clientVersion: this.clientVersion,
        serverVersion: '',
        tickHz: serverMeta.tickHz,
        mapId: serverMeta.mapId,
        screenRefreshHz: guessRefreshHz(),
        pingMsAtExport: this.transport.pingFor(localCharId),
        roster: this.transport.match.members.map((m) => ({
          charId: m.id,
          name: m.name,
          team: m.team,
          isNpc: false,
          isLocal: m.isLocal
        }))
      };

      const dump: CombinedDump = { meta, client: clientProfile, server: serverProfile };
      triggerDownload(dump);
    } finally {
      this.exporting = false;
    }
  }
}

// --------------------------------------------------------------------------
// helpers

function formatDur(ms: number): string {
  const total = Math.max(0, ms / 1000);
  const mm = Math.floor(total / 60).toString().padStart(2, '0');
  const ss = (total - Math.floor(total / 60) * 60).toFixed(1).padStart(4, '0');
  return `${mm}:${ss}`;
}

function inRateLast1s(wallTimes: number[]): number {
  if (wallTimes.length === 0) return 0;
  const last = wallTimes[wallTimes.length - 1]!;
  let count = 0;
  for (let i = wallTimes.length - 1; i >= 0; i--) {
    if (wallTimes[i]! >= last - 1000) count += 1;
    else break;
  }
  return count;
}

function randomSessionId(): string {
  const bytes = new Uint8Array(8);
  (crypto as Crypto).getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function guessRefreshHz(): number {
  // Best-effort: the browser doesn't expose the panel refresh rate directly.
  // Users can note this manually if the auto-guess is wrong.
  // Approximate via rAF deltas — deferred to first measurement or stored 0.
  return 0;
}

function triggerDownload(dump: CombinedDump): void {
  const json = JSON.stringify(dump);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const name = `netcode-profile-${stamp}-${dump.meta.sessionId.slice(0, 8) || 'adhoc'}.json`;
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Singleton wrapper — makes mounting trivial from main.ts. */
let _hud: ProfilerHud | null = null;

export function mountProfilerHud(opts: ProfilerHudOpts): ProfilerHud {
  if (_hud) {
    _hud.setTransport(opts.transport);
    return _hud;
  }
  _hud = new ProfilerHud(opts);
  return _hud;
}

export function unmountProfilerHud(): void {
  if (_hud) {
    _hud.dispose();
    _hud = null;
  }
}
